function pad(value: number) {
  return value.toString().padStart(2, "0")
}

// Milliseconds a given instant is offset from UTC in the target zone. Positive
// east of UTC. Computed by formatting the instant's wall-clock components in the
// zone and comparing them to the same components read as UTC.
function zoneOffsetMs(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant)

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value)

  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") === 24 ? 0 : get("hour"),
    get("minute"),
    get("second"),
  )

  return asUtc - instant.getTime()
}

// A "YYYY-MM-DDTHH:mm" wall-clock string interpreted as being in `timeZone`,
// returned as an absolute UTC ISO string. Single-pass offset correction; the
// DST fall-back hour is ambiguous but acceptable for scheduling.
export function zonedWallClockToIso(localDateTime: string, timeZone: string) {
  if (!localDateTime) return null

  const [datePart, timePart] = localDateTime.split("T")
  if (!datePart || !timePart) return null

  const [year, month, day] = datePart.split("-").map(Number)
  const [hour, minute] = timePart.split(":").map(Number)
  if ([year, month, day, hour, minute].some(Number.isNaN)) return null

  const guess = Date.UTC(year, month - 1, day, hour, minute, 0)
  const offset = zoneOffsetMs(new Date(guess), timeZone)
  return new Date(guess - offset).toISOString()
}

// An absolute UTC ISO string rendered as a "YYYY-MM-DDTHH:mm" wall-clock string
// in `timeZone`, suitable for a datetime-local input value.
export function isoToZonedWallClock(
  value: string | null | undefined,
  timeZone: string,
) {
  if (!value) return ""

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ""

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(parsed)

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ""

  const hour = get("hour") === "24" ? "00" : get("hour")
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`
}

export function inHoursWallClock(hours: number, timeZone: string) {
  const target = new Date(Date.now() + hours * 60 * 60 * 1000)
  return isoToZonedWallClock(target.toISOString(), timeZone)
}

export function tomorrowMorningWallClock(timeZone: string, hour = 9) {
  const todayWallClock = isoToZonedWallClock(new Date().toISOString(), timeZone)
  const [datePart] = todayWallClock.split("T")
  const [year, month, day] = datePart.split("-").map(Number)

  const tomorrow = new Date(Date.UTC(year, month - 1, day + 1))
  return `${tomorrow.getUTCFullYear()}-${pad(tomorrow.getUTCMonth() + 1)}-${pad(
    tomorrow.getUTCDate(),
  )}T${pad(hour)}:00`
}
